/**
 * DesignerNode - Freeform Interface Designer Node
 *
 * A React Flow node that contains a grid-based canvas where users can
 * place and arrange UI elements (text, images, buttons). This is the
 * foundation for "publishable interfaces" - designs that can be exported
 * as standalone web pages.
 *
 * Design principles:
 * - Header is draggable by React Flow (for moving the node)
 * - Canvas area uses `nodrag nopan` to let dnd-kit handle element drags
 * - Multi-layered shadows for depth (matches other BlinkinFlow nodes)
 * - Yjs integration for real-time collaboration
 */

import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import type { DesignerNodeData, DesignerElement } from '@deep-agents/shared-types';
import { DesignerCanvas } from './DesignerCanvas';
import type { DesignerNodeRuntimeData } from './types';
import {
  DEFAULT_GRID_COLS,
  DEFAULT_GRID_ROWS,
  DEFAULT_CELL_WIDTH,
  DEFAULT_CELL_HEIGHT,
} from './constants';

// Type for the React Flow node
type DesignerNodeType = Node<DesignerNodeData, 'designer'>;

export const DesignerNode = memo(({ id, data, selected, dragging }: NodeProps<DesignerNodeType>) => {
  // Destructure data with defaults
  const {
    title = 'New Designer',
    elements = [],
    gridCols = DEFAULT_GRID_COLS,
    gridRows = DEFAULT_GRID_ROWS,
    cellWidth = DEFAULT_CELL_WIDTH,
    cellHeight = DEFAULT_CELL_HEIGHT,
    nodesMap,
    makeChange,
  } = data as DesignerNodeRuntimeData;

  // Local state for title editing
  const [localTitle, setLocalTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);

  // Sync title with external changes
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // Dynamic shadow based on state (matches other nodes)
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // Show controls when hovered or selected
  const showControls = isHovered || selected;

  // Handle title change with Yjs persistence
  const handleTitleChange = useCallback((newTitle: string) => {
    setLocalTitle(newTitle);

    if (!nodesMap || !makeChange) return;

    makeChange(() => {
      const existing = nodesMap.get(id);
      if (!existing) return;
      nodesMap.set(id, {
        ...existing,
        data: {
          ...existing.data,
          title: newTitle,
        },
      });
    });
  }, [id, nodesMap, makeChange]);

  // Handle elements change with Yjs persistence
  const handleElementsChange = useCallback((newElements: DesignerElement[]) => {
    if (!nodesMap || !makeChange) return;

    makeChange(() => {
      const existing = nodesMap.get(id);
      if (!existing) return;
      nodesMap.set(id, {
        ...existing,
        data: {
          ...existing.data,
          elements: newElements,
        },
      });
    });
  }, [id, nodesMap, makeChange]);

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Receive data from upstream nodes"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-indigo-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Send design to downstream nodes"
      />

      {/* Floating header (above card, visible on hover/select) */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          z-30
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center gap-2 px-1">
          {/* Node type badge */}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-violet-100/80 rounded-lg border border-violet-200/50">
            <svg className="w-3.5 h-3.5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            <span className="text-[11px] font-semibold text-violet-700">Designer</span>
          </div>

          {/* Title input */}
          <div
            className="nodrag nopan"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={localTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Untitled Designer"
              className="
                px-2.5 py-1 text-[12px] font-semibold text-violet-700
                bg-gradient-to-r from-violet-50 to-indigo-50
                rounded-lg border border-violet-100/50
                outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-200
                placeholder:text-violet-300 placeholder:font-medium
                min-w-[80px] max-w-[200px] truncate
                transition-all
              "
              style={{ width: `${Math.max(80, Math.min(200, (localTitle.length || 12) * 8))}px` }}
            />
          </div>
        </div>
      </div>

      {/* Main card container - header is draggable, canvas is not */}
      <div
        className="
          relative bg-white rounded-2xl overflow-visible
          border transition-all duration-300
          border-neutral-200/60
        "
        style={{
          boxShadow: getShadowStyle(),
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
        }}
      >
        {/* Content area with padding */}
        <div className="p-4">
          <DesignerCanvas
            elements={elements}
            gridCols={gridCols}
            gridRows={gridRows}
            cellWidth={cellWidth}
            cellHeight={cellHeight}
            onElementsChange={handleElementsChange}
          />
        </div>
      </div>
    </div>
  );
});

DesignerNode.displayName = 'DesignerNode';
